import * as React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

import { TRUE_COLORS } from '../../theme';

/** Card with a consistent header (title, optional subtitle and action) and padded body. */
export const SectionCard = ({
  title,
  subtitle,
  action,
  children,
  noPadding,
  sx,
}: {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  children?: React.ReactNode;
  /** Let tables/lists run edge to edge. */
  noPadding?: boolean;
  sx?: object;
}) => (
  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', ...sx }}>
    {(title || action) && (
      <Stack direction="row" spacing={2} sx={{ px: 3, pt: 2.5, pb: noPadding ? 2 : 0, alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ minWidth: 0 }}>
          {title && <Typography variant="h6" component="h2">{title}</Typography>}
          {subtitle && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>{subtitle}</Typography>}
        </Box>
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Stack>
    )}
    {noPadding ? (
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>{children}</Box>
    ) : (
      <CardContent sx={{ flexGrow: 1, minHeight: 0 }}>{children}</CardContent>
    )}
  </Card>
);

/** KPI tile: label, big value, optional % change vs previous period and icon. */
export const StatCard = ({
  label,
  value,
  change,
  changeLabel,
  icon,
  loading,
  onClick,
}: {
  label: string;
  value: React.ReactNode;
  /** Fractional change, e.g. 0.12 = +12%. Omit to hide. */
  change?: number | null;
  changeLabel?: string;
  icon?: React.ReactNode;
  loading?: boolean;
  onClick?: () => void;
}) => {
  const hasChange = typeof change === 'number' && Number.isFinite(change);
  const color = !hasChange || change === 0 ? TRUE_COLORS.secondary : change! > 0 ? TRUE_COLORS.success : TRUE_COLORS.danger;
  const TrendIcon = !hasChange || change === 0 ? TrendingFlatIcon : change! > 0 ? TrendingUpIcon : TrendingDownIcon;
  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 150ms, transform 150ms',
        '&:hover': onClick ? { boxShadow: TRUE_COLORS.shadowHover, transform: 'translateY(-1px)' } : undefined,
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, minWidth: 0 }}>{label}</Typography>
          {icon && (
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px', display: 'grid', placeItems: 'center',
              bgcolor: 'rgba(20,37,44,0.06)', color: TRUE_COLORS.primary, '& svg': { fontSize: 20 },
            }}>
              {icon}
            </Box>
          )}
        </Stack>
        {loading ? (
          <Skeleton width="60%" height={40} />
        ) : (
          <Typography sx={{ fontSize: { xs: '1.25rem', sm: '1.75rem' }, fontWeight: 700, lineHeight: 1.2, color: 'text.primary', overflowWrap: 'anywhere' }}>{value}</Typography>
        )}
        {hasChange && !loading && (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 1 }}>
            <TrendIcon sx={{ fontSize: 16, color }} />
            <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
              {change! > 0 ? '+' : ''}{(change! * 100).toFixed(1)}%
            </Typography>
            {changeLabel && <Typography variant="body2" sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>{changeLabel}</Typography>}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

/** Friendly placeholder for lists/charts with no data yet. */
export const EmptyState = ({
  title,
  description,
  icon,
  action,
  compact,
}: {
  title: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  compact?: boolean;
}) => (
  <Stack spacing={1} sx={{ alignItems: 'center', textAlign: 'center', py: compact ? 3 : 6, px: 2 }}>
    <Box sx={{
      width: 48, height: 48, borderRadius: '14px', display: 'grid', placeItems: 'center', mb: 1,
      bgcolor: 'rgba(20,37,44,0.06)', color: TRUE_COLORS.secondary,
    }}>
      {icon ?? <InboxOutlinedIcon />}
    </Box>
    <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>{title}</Typography>
    {description && <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 360 }}>{description}</Typography>}
    {action && <Box sx={{ pt: 1 }}>{action}</Box>}
  </Stack>
);

export const currency = (value: number | string | null | undefined) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(value) || 0);

export const number = (value: number | string | null | undefined, digits = 0) =>
  new Intl.NumberFormat('es-MX', { maximumFractionDigits: digits }).format(Number(value) || 0);

/**
 * A number field that shows what is typed (no "050" left behind, no jumping caret, a comma works as the decimal
 * point) and sends the number out, or null when it's empty. Changes from outside (a reset, a saved record) show up;
 * the field's own edits are not rewritten while typing.
 */
export const NumberField = ({ value, onChange, decimals = true, ...props }: Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  /** false for whole numbers only (visits, quantities). */
  decimals?: boolean;
}) => {
  const shown = (v: number | null | undefined) => (v == null ? '' : String(v));
  const [text, setText] = React.useState(shown(value));
  const own = React.useRef<number | null | undefined>(value);
  React.useEffect(() => {
    if (value === own.current) return;
    own.current = value;
    setText(shown(value));
  }, [value]);
  return (
    <TextField
      {...props}
      value={text}
      onChange={e => {
        const raw = e.target.value.replace(',', '.').replace(/^0+(?=\d)/, '');
        if (!(decimals ? /^\d*\.?\d{0,2}$/ : /^\d*$/).test(raw)) return;
        const next = raw === '' || raw === '.' ? null : Number(raw);
        own.current = next;
        setText(raw);
        onChange(next);
      }}
      slotProps={{ ...props.slotProps, htmlInput: { inputMode: decimals ? 'decimal' : 'numeric', ...(props.slotProps?.htmlInput as object) } }}
    />
  );
};

/** Simple confirm dialog for destructive or important actions. */
export const ConfirmDialog = ({
  open,
  title,
  description,
  confirmLabel = 'Confirmar',
  danger,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}) => {
  const [busy, setBusy] = React.useState(false);
  const confirm = async () => {
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      {description && (
        <DialogContent>
          <DialogContentText>{description}</DialogContentText>
        </DialogContent>
      )}
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button
          onClick={confirm}
          variant="contained"
          disabled={busy}
          sx={danger ? { bgcolor: TRUE_COLORS.danger, '&:hover': { bgcolor: '#a93226' } } : undefined}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
